// /home/dev/arka_dev/arkadu-ui3/src/components/LoadingScreen.js
import { Box, LinearProgress, Typography } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import NProgress from "nprogress";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";

const Root = styled(Box, {
  shouldForwardProp: (prop) =>
    !["embedded", "withBackdrop", "maxWidth", "darkMode"].includes(prop),
})(({ embedded, withBackdrop, maxWidth, darkMode, theme }) => ({
  position: embedded ? "relative" : "fixed",
  inset: embedded ? "auto" : 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: embedded ? "auto" : "100dvh",
  width: "100%",
  background: withBackdrop
    ? embedded
      ? "transparent"
      : darkMode
        ? "linear-gradient(180deg, rgba(11,16,28,0.76) 0%, rgba(11,16,28,0.86) 100%)"
        : "linear-gradient(180deg, rgba(241,245,249,0.92) 0%, rgba(226,232,240,0.95) 100%)"
    : darkMode
      ? `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.96,
        )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
      : "linear-gradient(135deg, #f5f7fa 0%, #e8ebf0 100%)",
  overflow: "hidden",
  zIndex: embedded ? "auto" : 1300,
  padding: embedded ? 0 : 16,
  "& .LoadingScreen-card": {
    maxWidth: maxWidth || 560,
  },
}));

const AnimatedBackdrop = styled("div", {
  shouldForwardProp: (prop) => !["withBackdrop", "darkMode"].includes(prop),
})(({ withBackdrop, darkMode, theme }) => ({
  position: "absolute",
  inset: "-10%",
  background: withBackdrop
    ? darkMode
      ? "repeating-linear-gradient(135deg, rgba(148,163,184,0.08) 0px, rgba(148,163,184,0.08) 2px, transparent 2px, transparent 12px)"
      : "repeating-linear-gradient(135deg, rgba(15,23,42,0.04) 0px, rgba(15,23,42,0.04) 2px, transparent 2px, transparent 12px)"
    : darkMode
      ? `repeating-linear-gradient(135deg, ${alpha(
          theme.palette.common.white,
          0.045,
        )} 0px, ${alpha(
          theme.palette.common.white,
          0.045,
        )} 2px, transparent 2px, transparent 10px)`
      : "repeating-linear-gradient(135deg, rgba(100,116,139,0.03) 0px, rgba(100,116,139,0.03) 2px, transparent 2px, transparent 10px)",
  pointerEvents: "none",
}));

const Card = styled(Box, {
  shouldForwardProp: (prop) => !["withBackdrop", "darkMode"].includes(prop),
})(({ withBackdrop, darkMode, theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
  padding: 24,
  borderRadius: 16,
  border: withBackdrop
    ? "1px solid rgba(255,255,255,0.12)"
    : darkMode
      ? `1px solid ${alpha(theme.palette.divider, 0.45)}`
      : "1px solid rgba(226,232,240,0.8)",
  background: withBackdrop
    ? "rgba(15,23,42,0.72)"
    : darkMode
      ? alpha(theme.palette.background.paper, 0.9)
      : "rgba(255,255,255,0.85)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  boxShadow: withBackdrop
    ? "0 18px 48px rgba(2,6,23,0.38), inset 0 1px 0 rgba(255,255,255,0.06)"
    : darkMode
      ? "0 8px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 4px 16px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
  "@media (min-width: 600px)": { padding: 32 },
}));

const AccentBar = styled("div")({
  height: 4,
  width: "100%",
  borderRadius: 999,
  background: "linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)",
});

const StyledLinear = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== "withBackdrop",
})(({ withBackdrop }) => ({
  height: 8,
  borderRadius: 999,
  backgroundColor: withBackdrop
    ? "rgba(255,255,255,0.12)"
    : "rgba(59,130,246,0.12)",
  overflow: "hidden",
  "& .MuiLinearProgress-bar": {
    borderRadius: 999,
    background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
    willChange: "transform",
  },
}));

const LoadingScreen = React.memo(function LoadingScreen({
  message,
  title,
  subtitle,
  embedded = false,
  withBackdrop = false,
  suppressWhenGlobalLoading = true,
  forceStandalone = false,
  maxWidth,
  minHeight,
  ...rest
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isLoading: isGlobalLoading, theme: settingsTheme } = useSelector(
    (state) => state.settings,
  );

  const isDarkMode =
    theme.palette.mode === "dark" ||
    String(settingsTheme).toUpperCase() === "DARK";

  const shouldSuppress =
    !embedded &&
    suppressWhenGlobalLoading &&
    !forceStandalone &&
    Boolean(isGlobalLoading);

  useEffect(() => {
    if (embedded) {
      return undefined;
    }
    NProgress.configure({
      showSpinner: false,
      trickleSpeed: 120,
      minimum: 0.1,
    });
    NProgress.start();
    return () => NProgress.done();
  }, [embedded]);

  const text = useMemo(
    () =>
      message && typeof message === "string"
        ? message
        : "Loading, please wait...",
    [message],
  );

  if (shouldSuppress) {
    return null;
  }

  return (
    <Root
      role="alert"
      aria-busy="true"
      aria-live="polite"
      embedded={embedded}
      withBackdrop={withBackdrop}
      darkMode={isDarkMode}
      maxWidth={maxWidth}
      sx={minHeight ? { minHeight } : undefined}
      {...rest}
    >
      <AnimatedBackdrop withBackdrop={withBackdrop} darkMode={isDarkMode} />
      <Card
        className="LoadingScreen-card"
        withBackdrop={withBackdrop}
        darkMode={isDarkMode}
      >
        <AccentBar />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: withBackdrop
                ? "rgba(255,255,255,0.96)"
                : isDarkMode
                  ? theme.palette.text.primary
                  : "#1e293b",
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
            }}
          >
            {title || t("Cargando...")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: withBackdrop
                ? "rgba(255,255,255,0.72)"
                : isDarkMode
                  ? alpha(theme.palette.text.secondary, 0.9)
                  : "#64748b",
              fontSize: { xs: 11, sm: 12 },
            }}
          >
            {subtitle || t("Optimizando la experiencia para ti...")}
          </Typography>
        </Box>

        <StyledLinear color="primary" withBackdrop={withBackdrop} />

        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            color: withBackdrop
              ? "rgba(255,255,255,0.84)"
              : isDarkMode
                ? alpha(theme.palette.text.secondary, 0.92)
                : "#475569",
            fontSize: { xs: 13, sm: 14 },
          }}
        >
          {text}
        </Typography>
      </Card>
    </Root>
  );
});

export default LoadingScreen;
